import { Component, OnInit, ViewChild } from '@angular/core';
import { AbstractControlOptions, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { StatusCode } from '@app/_enums/status-code';
import { BaseParentComponent } from '@components/_base/base-parent/base-parent.component';
import { Status } from '@models/status.model';
import { TranslateService } from '@ngx-translate/core';
import { NomenclatureService } from '@services/nomenclature.service';
import { BehaviorSubject, EMPTY, Subscription, forkJoin } from 'rxjs';
import { catchError, concatMap, repeatWhen, tap } from 'rxjs/operators';
import { CashbackService } from '@app/cashbacks/_services/cashback.service';
import { PermissionsService } from '@app/login/_services/permissions.service';
import { Cashback } from '../_models/cashback.model';
import { displayError } from '@app/_utils/error-util';
import { UIEventCustom } from '@app/_utils/ui-event-util';
import { Service } from '@app/services/_models/service.model';
import { PriceAttribute } from '@models/price-attribute.model';
import { VersionData } from '@models/version-data.model';
import { ServiceType } from '@models/service-type.model';

@Component({
  selector: 'app-add-edit-cashback',
  templateUrl: './add-edit-cashback.component.html',
  styleUrls: ['./add-edit-cashback.component.css']
})
export class AddEditCashbackComponent extends BaseParentComponent implements OnInit {
  // Units
  statuses:       Status[];
  defaultStatus:  Status | null;
  cashback:       Cashback;
  services:       Service[];
  serviceTypes:   ServiceType[];
  filteredServices: Service[];

  // Constants
  readonly INACTIVE_STATUS_CODE:      string = 'INACTIVE';
  readonly ACTIVE_STATUS_CODE:        string = 'ACTIVE';

  // Payload
  private subscriptions = new Subscription();
  todayDate:            Date = new Date();
  statusCode:           string;
  cashbackId:           number;

  // Booleans
  dataLoaded        = false;
  disableActionBtn  = false;

  // Form
  mainDataForm = this.formBuilder.group({
    id:                     [null],
    status:                 [null, Validators.required],
    serviceType:            [null, Validators.required],
    service:                [null, Validators.required],
    moneyValueOfPoint:      [null, [Validators.required, Validators.min(0.01)]],
    priceAttributeAmountDds: [null, [Validators.required, Validators.min(0.01)]],
    priceAttributeKind:      [null, Validators.required],
    versionData:            [null]
  });

  // Observables
  loadCashback$ = this.route.queryParams.pipe(
    concatMap(params => {
      return forkJoin([
        this.cashbackService.findCashbackById(params['id']),
        this.nomenclatureService.getStatusesByType(this.checkStatuses()),
        this.nomenclatureService.findAllValidServices(),
        this.nomenclatureService.getServiceTypes()
      ]).pipe(
        tap(() => this.cashbackId = params['id']),
        tap(([cashback, statuses, services, serviceTypes]) => {
          this.statuses = statuses as Status[];
          this.services = services as Service[];
          this.serviceTypes = serviceTypes as ServiceType[];
          this.filteredServices = this.services;

          this.cashback = cashback as Cashback;
          this.statusCode = (cashback as Cashback)?.status?.code;
          this.defaultStatus = this.statuses.find(status => status.code === this.INACTIVE_STATUS_CODE) || null;

          this.mainDataForm.patchValue(cashback as any);

          this.mainDataForm.get('status')?.patchValue(!!cashback && !!(cashback as Cashback).status ? (cashback as Cashback).status : this.defaultStatus);

          // Patch service type from the cashback's service
          if (cashback && (cashback as Cashback).service && (cashback as Cashback).service.serviceType) {
            this.mainDataForm.get('serviceType')?.patchValue((cashback as Cashback).service.serviceType);
            
            // Filter services based on the service type from the cashback
            this.filteredServices = this.services.filter(service => 
              service.serviceType && service.serviceType.id === (cashback as Cashback).service.serviceType.id
            );
          } else {
            // For new cashbacks, show all services initially
            this.filteredServices = this.services;
          }

          // Handle price attribute validation based on kind
          if (cashback?.priceAttributeKind == 'P') {
            this.mainDataForm.get('priceAttributeAmountDds')?.setValidators([Validators.required, Validators.min(1), Validators.max(100)]);
            this.mainDataForm.get('priceAttributeAmountDds')?.updateValueAndValidity();
          } else if (cashback?.priceAttributeKind == 'V') {
            this.mainDataForm.get('priceAttributeAmountDds')?.setValidators([Validators.required, Validators.min(1), Validators.max(99)]);
            this.mainDataForm.get('priceAttributeAmountDds')?.updateValueAndValidity();
          } else {
            this.mainDataForm.get('priceAttributeAmountDds')?.clearValidators();
            this.mainDataForm.get('priceAttributeAmountDds')?.updateValueAndValidity();
          }

          this.mainDataForm.get('priceAttributeAmountDds')?.patchValue(cashback?.priceAttributeAmountDds);

          if (this.statusCode == this.ACTIVE_STATUS_CODE) {
            this.mainDataForm.disable();
          }
          this.dataLoaded = true;
        }),
        catchError((err: any) => {
          displayError(err);
          this.dataLoaded = false;
          this.errorMessageSubject.next(this.translateService.instant('messages.errorLoadingData'));
          return EMPTY;
        }),
        repeatWhen(() => this.reload$)
      );
    })
  );

  constructor(
    private router:               Router,
    private route:                ActivatedRoute,
    private formBuilder:          FormBuilder,
    private uiEvent:              UIEventCustom,
    public perms:                 PermissionsService,
    private translateService:     TranslateService,
    private cashbackService:      CashbackService,
    private nomenclatureService:  NomenclatureService
  ) {
    super(router);
  }

  ngOnInit() {
    this.showValidationsSubscription = this.showValidations$.subscribe({
      next: (data) => this.showValidations = data
    });
  }

  async save(deactivate: boolean) {
    if (this.mainDataForm.get('status')?.value?.code === this.INACTIVE_STATUS_CODE) {
      if (!this.partualValidation()) {
        this.uiEvent.displayUIError();
        this.showValidationsSubject.next(true);
        this.showFullValidationSubject.next(false);
        return;
      }
    } else {
      if (!deactivate) {
        if (!this.fullValidation()) {
          this.uiEvent.displayUIError();
          this.showValidationsSubject.next(true);
          this.showFullValidationSubject.next(true);
          return;
        }
      }
    }

    this.disableActionBtn = true;
    this.cashbackService.saveCashback(this.constructCashback(), deactivate).subscribe({
      next: () => {
        this.uiEvent.displayUISuccess();
        this.disableActionBtn = false;
        this.router.navigate(['/list-cashbacks']);
      },
      error: err => {
        displayError(err);
        this.disableActionBtn = false;
        this.uiEvent.displayUIError();
      }
    });
  }

  private partualValidation() {
    return this.mainDataForm.valid;
  }

  private fullValidation() {
    return this.mainDataForm.valid;
  }

  checkStatuses() {
    return StatusCode.PromotionDiscount;
  }

  changePriceAttrKind(event: any) {
    let type = event.target.value;

    if (type == 'P') {
      this.mainDataForm.get('priceAttributeAmountDds')?.setValidators([Validators.required, Validators.min(1), Validators.max(100)]);
      this.mainDataForm.get('priceAttributeAmountDds')?.updateValueAndValidity();
    } else if (type == 'V') {
      this.mainDataForm.get('priceAttributeAmountDds')?.setValidators([Validators.required, Validators.min(1), Validators.max(99)]);
      this.mainDataForm.get('priceAttributeAmountDds')?.updateValueAndValidity();
    }
  }

  onServiceTypeChange(event: any) {
    const selectedServiceType = this.mainDataForm.get('serviceType')?.value;
    
    if (selectedServiceType && selectedServiceType.id) {
      this.filteredServices = this.services.filter(service => 
        service.serviceType && service.serviceType.id === selectedServiceType.id
      );
    } else {
      this.filteredServices = this.services;
    }
    
    // Clear the service selection when service type changes
    this.mainDataForm.get('service')?.setValue(null);
  }

  private constructCashback(): Cashback {
    const cashback = this.mainDataForm.value as Cashback;
    return cashback;
  }

  public deactivate() {
    this.save(true);
  }

  get mainForm() {
    return this.mainDataForm.controls;
  }

  get isDisabled() {
    return this.statusCode == this.ACTIVE_STATUS_CODE
  }

  get canAddEdit() {
    return this.perms.hasAccess(this.perms.CAN_VIEW_MARKETING_MENU);
  }
}
